import React, { useState, memo } from 'react';
import { TableRow, TableCell, Box, Typography, Chip, IconButton, Collapse } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { INotificationLogResponse } from 'services/GeneralLogs/NotificationLogService/types';
import { formatToLocalTime, getPlatformIcon } from '../helpers';
import { COLORS } from './Constants';

const {
  backgroundColorDarkTheme,
  tableBackgroundColor,
  tableHeaderColor,
  borderColorDarkTheme,
  fontColorDarkTheme
} = COLORS;

const NotificationLogRow = ({ row }: { row: INotificationLogResponse }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const defaultCellStyles = {
    color: fontColorDarkTheme,
    borderBottom: `1px solid ${borderColorDarkTheme}`
  };

  return (
    <React.Fragment>
      {/* LINHA PRINCIPAL */}
      <TableRow 
        onClick={() => setOpen(!open)}
        sx={{ 
          cursor: 'pointer',
          backgroundColor: tableBackgroundColor,
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
        }}
      >
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '13px', ...defaultCellStyles }}>
          {formatToLocalTime(row.createdAt)}
        </TableCell>
        
        <TableCell sx={defaultCellStyles}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getPlatformIcon(row.platform)}
            <Typography variant="body2" sx={{ color: fontColorDarkTheme }}>
              {row.platform}
            </Typography>
          </Box>
        </TableCell>
        
        <TableCell sx={defaultCellStyles}>
          <Chip 
            label={row.isSuccess ? t('notificationLogs.status.success') : t('notificationLogs.status.failure')}
            size="small"
            color={row.isSuccess ? 'success' : 'error'}
            variant={row.isSuccess ? 'outlined' : 'filled'}
            sx={{ fontWeight: 'bold', fontSize: '11px', borderRadius: '4px' }} 
          />
        </TableCell>
        
        <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...defaultCellStyles }}>
          {row.subjectName || t('common.unknown')}
        </TableCell>
        
        <TableCell align="right" sx={defaultCellStyles}>
          <IconButton size="small" sx={{ color: fontColorDarkTheme }}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
      </TableRow>

      {/* ÁREA EXPANDIDA (DETALHES) */}
      <TableRow
        sx={{
          backgroundColor: open ? backgroundColorDarkTheme : tableBackgroundColor,
          '& > td': {
            padding: 0,
            borderBottom: open ? `1px solid ${borderColorDarkTheme}` : 'none',
          },
        }}
      >
        <TableCell colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, fontFamily: 'monospace', fontSize: '13px', backgroundColor: tableHeaderColor, p: 2, borderRadius: 2, border: `1px solid ${borderColorDarkTheme}` }}>
              
              <Grid2 container spacing={1} sx={{ mb: 1 }}>
                <Grid2 size={{ xs: 3 }} sx={{ color: 'gray', fontWeight: 'bold' }}>{t('notificationLogs.details.faceRecognitionEventId')}:</Grid2>
                <Grid2 size={{ xs: 9 }}>
                  <Chip 
                    label={row.faceRecognitionEventId} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ color: '#58a6ff', borderColor: borderColorDarkTheme, borderRadius: '4px' }} 
                  />
                </Grid2>
              </Grid2>

              <Grid2 container spacing={1} sx={{ mb: 1 }}>
                <Grid2 size={{ xs: 3 }} sx={{ color: 'gray', fontWeight: 'bold' }}>{t('notificationLogs.details.recipients')}:</Grid2>
                <Grid2 size={{ xs: 9 }} sx={{ color: fontColorDarkTheme }}>{row.recipients}</Grid2>
              </Grid2>

              <Grid2 container spacing={1} sx={{ mb: 1 }}>
                <Grid2 size={{ xs: 3 }} sx={{ color: 'gray', fontWeight: 'bold' }}>{t('notificationLogs.details.subjectType')}:</Grid2>
                <Grid2 size={{ xs: 9 }} sx={{ color: fontColorDarkTheme }}>{row.subjectType}</Grid2>
              </Grid2>

              {row.errorMessage && (
                <Grid2 container spacing={1} sx={{ mt: 1 }}>
                  <Grid2 size={{ xs: 3 }} sx={{ color: '#ffb4ab', fontWeight: 'bold' }}>{t('notificationLogs.details.errorMessage')}:</Grid2>
                  <Grid2 size={{ xs: 9 }}>
                    <Box sx={{ 
                      backgroundColor: 'rgba(211, 47, 47, 0.1)', border: '1px solid #d32f2f', color: '#ffb4ab', 
                      px: 1, py: 0.5, borderRadius: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap' 
                    }}>
                      {row.errorMessage}
                    </Box>
                  </Grid2>
                </Grid2>
              )}

            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default memo(NotificationLogRow);